/**
 * Reverb.js — procedural convolution reverb.
 *
 * The impulse response is generated numerically rather than sampled: decorrelated
 * noise shaped by an exponential decay, run through a time-varying one-pole lowpass
 * so high frequencies die before low ones (real rooms absorb HF fastest), with a
 * sparse set of early reflections stamped on top. The early reflections are what
 * actually tell the ear the size of the room; the diffuse tail only tells it how
 * reflective the surfaces are.
 *
 * Generation is pure arithmetic on a Float32Array with a seeded RNG, so the same
 * seed produces a bit-identical IR in the live context and in any OfflineAudioContext
 * used for verification.
 */

import { makeRng, biquad, gain, chain, clamp } from './synth/dsp.js';

export const IR_PRESETS = {
  // Concrete industrial interior — the default for OPERATION BLACKOUT.
  room: {
    seconds: 1.35, decayDb: 62, predelay: 0.011, damping: 1.9,
    earlyGain: 0.5, diffusion: 0.85, seed: 0x1337,
    early: [0.0089, 0.0137, 0.0191, 0.0263, 0.0341, 0.0437, 0.0559, 0.0713],
  },
  // Tight corridor / small room: short, bright, strong slapback.
  tight: {
    seconds: 0.55, decayDb: 60, predelay: 0.005, damping: 3.2,
    earlyGain: 0.75, diffusion: 0.6, seed: 0x2b1d,
    early: [0.0043, 0.0071, 0.0103, 0.0149, 0.0197, 0.0251],
  },
  // Open exterior: long, dark, sparse — mostly distant reflections.
  outdoor: {
    seconds: 2.1, decayDb: 66, predelay: 0.022, damping: 1.1,
    earlyGain: 0.28, diffusion: 0.95, seed: 0x77a3,
    early: [0.0231, 0.0379, 0.0521, 0.0733, 0.0977, 0.1291],
  },
};

/**
 * Build a stereo impulse response.
 * @returns {AudioBuffer}
 */
export function buildImpulseResponse(ac, preset = IR_PRESETS.room) {
  const p = { ...IR_PRESETS.room, ...preset };
  const sr = ac.sampleRate;
  const n = Math.max(64, Math.floor(p.seconds * sr));
  const buf = ac.createBuffer(2, n, sr);
  // -decayDb over the whole IR length.
  const k = (p.decayDb / 20) * Math.LN10 / p.seconds;

  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    // Independent seeds per channel decorrelate the tail, which is what produces
    // width. Correlated noise in both ears collapses the reverb to the centre.
    const rng = makeRng(p.seed + ch * 0x9e3779b9);
    let lp = 0;
    let hp = 0;

    for (let i = 0; i < n; i++) {
      const t = i / sr;
      let s = rng() * 2 - 1;

      // Time-varying one-pole: cutoff coefficient shrinks as the tail ages, so the
      // late reverb is progressively darker. This single line does most of the work
      // in making the result sound like air and concrete rather than like noise.
      const c = clamp(0.55 * Math.exp(-t * p.damping) + 0.035, 0.02, 0.9);
      lp += c * (s - lp);
      s = lp * (1.2 + 0.8 * (1 - c));

      // One-pole DC/rumble trap.
      hp += 0.0022 * (s - hp);
      s -= hp;

      // Build-up: energy should ramp in over the first few ms, not start at full
      // density, otherwise the reverb onset itself sounds like a transient.
      const build = 1 - Math.exp(-t * (60 + 240 * (1 - p.diffusion)));
      d[i] = s * Math.exp(-k * t) * build;
    }

    // Early reflections: discrete, slightly filtered impulses with alternating sign.
    for (let e = 0; e < p.early.length; e++) {
      const jitter = 1 + (rng() - 0.5) * 0.22;
      const at = Math.floor((p.predelay + p.early[e] * jitter) * sr);
      if (at >= n - 8) continue;
      const g = p.earlyGain * Math.pow(0.76, e) * (e % 2 ? -1 : 1) * (0.7 + rng() * 0.6);
      // A 5-sample smear instead of a bare impulse: a single sample reads as a click.
      for (let j = 0; j < 6; j++) {
        const w = Math.exp(-j * 0.55) * (1 - j / 8);
        d[at + j] += g * w * (1 - 2 * rng() * 0.25);
      }
    }

    // Silence the last 4% so the convolution tail can never end abruptly.
    const fade = Math.floor(n * 0.04);
    for (let i = n - fade; i < n; i++) {
      const u = (i - (n - fade)) / fade;
      d[i] *= 0.5 + 0.5 * Math.cos(Math.PI * u);
    }
  }

  // Peak-normalise; ConvolverNode.normalize then handles equal-power scaling.
  let max = 0;
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < n; i++) { const a = Math.abs(d[i]); if (a > max) max = a; }
  }
  if (max > 1e-8) {
    const s = 0.95 / max;
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < n; i++) d[i] *= s;
    }
  }
  return buf;
}

/**
 * A reverb send bus: `input` -> highpass -> predelay -> convolver -> `output`.
 *
 * The highpass matters more than it looks: gunfire has enormous sub-100 Hz energy and
 * feeding it into a 1.3 s tail turns every firefight into mud. Rooms do reflect low
 * frequencies, but the ear reads the low end as *direct* sound, so keeping it dry
 * reads as "tight" rather than "small".
 */
export class Reverb {
  constructor(ac, presetName = 'room') {
    this.ac = ac;
    this.input = gain(ac, 1);
    this.highpass = biquad(ac, 'highpass', 190, 0.6);
    this.tone = biquad(ac, 'lowpass', 7600, 0.7);
    this.predelay = ac.createDelay(0.2);
    this.convolver = ac.createConvolver();
    this.convolver.normalize = true;
    this.output = gain(ac, 1);

    chain(this.input, this.highpass, this.tone, this.predelay, this.convolver, this.output);
    this.setPreset(presetName);
  }

  setPreset(name) {
    const p = IR_PRESETS[name] ?? IR_PRESETS.room;
    this.presetName = IR_PRESETS[name] ? name : 'room';
    this.convolver.buffer = buildImpulseResponse(this.ac, p);
    this.predelay.delayTime.value = p.predelay;
    return this;
  }

  /** Crossfade the wet return. `time` in seconds. */
  setWet(v, time = 0.4) {
    const now = this.ac.currentTime;
    const g = this.output.gain;
    try { g.cancelAndHoldAtTime(now); } catch { g.cancelScheduledValues(now); g.setValueAtTime(g.value, now); }
    g.linearRampToValueAtTime(Math.max(0, v), now + time);
    return this;
  }

  /** Darken or brighten the whole reverb return — cheap "material" control. */
  setTone(hz, time = 0.4) {
    const now = this.ac.currentTime;
    this.tone.frequency.linearRampToValueAtTime(Math.max(200, hz), now + time);
    return this;
  }

  dispose() {
    try {
      this.input.disconnect();
      this.output.disconnect();
      this.convolver.disconnect();
      this.convolver.buffer = null;
    } catch { /* context already gone */ }
  }
}
